/** Merge in-memory EA stream metrics over REST baseline. */
export function mergeMetrics(base = {}, live) {
  if (!live) return base;
  const isConnected = live.ea_connected !== undefined ? Boolean(live.ea_connected) : Boolean(base.ea_connected ?? true);
  const merged = {
    ...base,
    ...live,
    open_positions: isConnected ? (live.positions_count ?? live.open_positions ?? base.open_positions ?? 0) : 0,
    ea_connected: isConnected,
  };
  if (!isConnected) {
    merged.floating_pl = 0.0;
    merged.equity = Number(merged.balance || base.balance || 0);
  }
  merged.profit_today = live.profit_today ?? live.daily_pl ?? base.profit_today ?? base.daily_pl ?? 0;
  merged.withdrawals_today = live.withdrawals_today ?? base.withdrawals_today ?? 0;
  return merged;
}

/** Recompute fleet totals from overview fleet rows + live WS patches. */
export function sumFleetPnL(fleet, liveMetrics) {
  let floating = 0;
  let daily = 0;
  let equity = 0;
  let balance = 0;
  for (const row of fleet) {
    const live = liveMetrics[row.id];
    const isConnected = live?.ea_connected !== undefined ? Boolean(live.ea_connected) : Boolean(row.ea_connected);
    const rowBalance = Number(live?.balance ?? row.balance ?? 0);
    const rowFloating = isConnected ? Number(live?.floating_pl ?? row.floating_pl ?? 0) : 0;
    const rowEquity = isConnected ? Number(live?.equity ?? row.equity ?? (rowBalance + rowFloating)) : rowBalance;

    floating += rowFloating;
    daily += Number(live?.profit_today ?? live?.daily_pl ?? row.daily_pl ?? 0);
    equity += rowEquity;
    balance += rowBalance;
  }
  return {
    total_floating_pl: Math.round(floating * 100) / 100,
    total_daily_pl: Math.round(daily * 100) / 100,
    total_equity: Math.round(equity * 100) / 100,
    total_balance: Math.round(balance * 100) / 100,
  };
}

/** Check if an MT5 trade belongs to Dashboard manual / hedge orders (Magic 90001 or 999999). */
export function isDashboardTrade(trade) {
  if (!trade) return false;
  const magic = Number(trade.magic);
  if (magic === 90001 || magic === 999999) return true;
  if (trade.is_hedge === true || String(trade.is_hedge).toLowerCase() === "true") return true;
  const comment = String(trade.comment || "").toUpperCase();
  if (["HUB ORDER", "HUB HEDGE", "HEDGE", "HDG", "HUB"].some((kw) => comment.includes(kw))) return true;
  return false;
}

/** Aggregate live Dashboard Orders statistics & MTM across accounts. */
export function computeDashboardOrdersStats(fleet = [], liveMetrics = {}) {
  let count = 0;
  let buyCount = 0;
  let sellCount = 0;
  let totalLots = 0;
  let mtm = 0;

  for (const acc of fleet) {
    const live = liveMetrics[acc.id] || {};
    const openTrades = live.open_trades || acc.open_trades || [];
    for (const t of openTrades) {
      if (isDashboardTrade(t)) {
        count += 1;
        const lot = Number(t.lot ?? t.volume ?? 0);
        totalLots += lot;
        const typeStr = String(t.type ?? t.side ?? "").toUpperCase();
        if (typeStr === "0" || typeStr === "BUY") buyCount += 1;
        else if (typeStr === "1" || typeStr === "SELL") sellCount += 1;

        const profit = Number(t.profit ?? 0);
        const swap = Number(t.swap ?? 0);
        const comm = Number(t.commission ?? 0);
        mtm += (profit + swap + comm);
      }
    }
  }

  return {
    dashboard_orders_count: count,
    dashboard_buy_count: buyCount,
    dashboard_sell_count: sellCount,
    dashboard_lots: Math.round(totalLots * 100) / 100,
    dashboard_mtm: Math.round(mtm * 100) / 100,
  };
}

